import React, { forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Close } from '@mui/icons-material';
import { IconButton, Slide, useMediaQuery, useTheme } from '@mui/material';

const CustomDialogBox = ({
  open,
  handleClose,
  title,
  content,
  actions,
  disabled,
  customBodyRender,
  width,
  height,
  fullScreen,
  margin = 1,
  padding = 0,
  shouldAnimate = true,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const slideTransition = {
    TransitionComponent: Slide,
    TransitionProps: {
      direction: 'up',
    },
  };

  const dialogVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: 'easeInOut' } },
    exit: { opacity: 0, scale: 0.8, transition: { duration: 0.3, ease: 'easeInOut' } },
  };

  const dialogStyles = {
    width: isSmallScreen ? '90%' : width || '60%',
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: 'rgba(0, 0, 0, 0.16) 0px 3px 6px, rgba(0, 0, 0, 0.23) 0px 3px 6px',
    borderTop: '4px solid #007bff',
    margin: 'auto',
    overflow: 'auto',
  };
  const getButtonStyles = (action) => {
    switch (action.label) {
      case 'Approve':
        return {
          backgroundColor: '#28a745', // Green
          hoverColor: '#218838', // Darker green
        };
      case 'Reject':
        return {
          backgroundColor: 'red', // Red
          hoverColor: '#c82333', // Darker red
        };
      default:
        return {
          backgroundColor: '#007bff', // Default blue
          hoverColor: '#0056b3', // Darker blue
        };
    }
  };

  const buttonStyles = {
    textTransform: 'none',
    color: '#fff',
    '&:hover': {
      textTransform: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: '#ccc',
      color: '#999',
    },
  };

  const titleStyles = {
    padding: '5px',
    borderBottom: '1px solid #eee',
    fontSize: '1.8rem',
  };

  const contentStyles = {
    color: 'primary',
    padding: '5px',
    overflowY: 'scroll',
    height: height || 'auto',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  };

  return (
    <Dialog
      fullScreen
      fullWidth
      maxWidth='md'
      {...(isMobile && shouldAnimate ? slideTransition : {})}
      transitionDuration={300}
      open={open || false}
      onClose={handleClose}
      keepMounted
      aria-describedby='slide-up animation'
      sx={{ height: '100%', padding: 0, margin: 0 }}
      // PaperProps={{ sx: dialogStyles }}
    >
      {open && (
        <>
          <DialogContent sx={{ padding: padding, margin: margin }}>
            {title && (
              <>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
                  <Button
                    {...(open && { className: 'backButton' })}
                    onClick={handleClose}
                    startIcon={<Close sx={{ fontSize: '29px' }} />}
                    sx={{ mr: 0, pr: 0, textTransform: 'none', float: 'right', color: 'black' }}
                  ></Button>
                  <DialogTitle sx={titleStyles}>
                    <b>{title}</b>
                  </DialogTitle>
                </motion.div>
              </>
            )}
            <DialogContent sx={contentStyles}>{customBodyRender ? customBodyRender(content) : content}</DialogContent>
          </DialogContent>
          <DialogActions sx={{ marginTop: 'auto' }}>
            {actions?.map((action, index) => {
              const { backgroundColor, hoverColor } = getButtonStyles(action);
              return (
                <Button
                  fullWidth
                  key={index}
                  onClick={action.onClick}
                  disabled={action.label !== 'Close' && disabled}
                  sx={{
                    ...buttonStyles,
                    backgroundColor: backgroundColor,
                    '&:hover': {
                      backgroundColor: hoverColor,
                    },
                  }}
                >
                  {action.icon && <action.icon />}
                  {action.label}
                </Button>
              );
            })}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default CustomDialogBox;
